<template>
  <MarketCard class="offer">
    <OfferHeader :class="[classes.offerHeader, 'offer-header']">
      {{ offerHeader }}
    </OfferHeader>

    <div
      :class="[classes.offerWrapper]"
      :style="{ height: offerTextBox.height + 'px' }"
    >
      <div :class="[classes.offerText]">
        <ResizeListener
          :class="[classes.resize]"
          :onResize="onResize"
          :log="true"
        />
        <OfferText :class="classes.offerTextBit">
          <p :key="line" :style="offerStyle" v-for="line in offerLines">
            {{ line }}
          </p>
        </OfferText>
      </div>
    </div>
    <div :class="classes.offerButtonWrapper">
      <Button :class="[classes.offerButton, 'offer-button']" />
    </div>
  </MarketCard>
</template>

<script lang="ts">
import { computed, defineComponent, ref, watchEffect } from "vue";
import MarketCard from "../styled/MarketCard.vue";
import Button from "./Button.vue";
import OfferText from "../branded/OfferText.vue";
import OfferHeader from "../branded/OfferHeader.vue";
import ResizeListener from "../ResizeListener.vue";

import { AutoFittingText } from "fontfill/src/index";
import { createUseStyles } from "@fresh8/ad-data-composition/src/theme";
import { injectBrand } from "@fresh8/ad-data-composition/src/brand";
import { useGetOffer } from "../../hooks/useDisplayOffer";
import { titleCase } from "@fresh8/ad-data-composition/src/lib/formatText";

const { useStyles } = createUseStyles(
  {
    textWrapper: {
      flex: "1 1 0",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center"
    },
    offerHeader: {
      fontSize: "12px",
      textAlign: "center",
      margin: "6px 0",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
      overflow: "hidden",
      padding: "0 6px",
      boxSizing: "border-box",
      lineHeight: 1
    },
    offerWrapper: {
      flex: "1 1 0",
      minHeight: "0px",
      padding: "0 6px",
      overflow: "hidden",
      boxSizing: "border-box",
      flexDirection: "column",
      display: "flex"
    },
    offerText: {
      textAlign: "center",
      display: "flex",
      justifyContent: "center",
      flexDirection: "column",
      flex: 1,
      overflow: "hidden",
      position: "relative"
    },
    offerTextBit: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      position: "relative"
    },
    offerButton: {
      height: "30px",
      padding: "8px"
    },
    offerButtonWrapper: {
      padding: "6px"
    },
    resize: {
      pointerEvents: "none",
      zIndex: -99
    }
  },
  undefined
);

export default defineComponent({
  name: "OfferCard",
  components: {
    MarketCard,
    Button,
    OfferText,
    OfferHeader,
    ResizeListener
  },
  setup() {
    const { classes } = useStyles(ref(undefined));
    const { getFontStyle, adTemplate } = injectBrand();
    const displayOffer = useGetOffer();

    const fontStyle = computed(() =>
      getFontStyle(adTemplate.value.offer?.body, true)
    );

    const targetString = computed(() => {
      if (fontStyle.value.textTransform === "uppercase") {
        return displayOffer.value && displayOffer.value.body.toUpperCase();
      }

      if (fontStyle.value.textTransform === "capitalize") {
        return displayOffer.value && titleCase(displayOffer.value.body);
      }

      if (fontStyle.value.textTransform === "lowercase") {
        return displayOffer.value && displayOffer.value.body.toLowerCase();
      }

      return displayOffer.value && displayOffer.value.body;
    });

    const defaultFontFamily =
      "'Roboto','Arial','Helvetica Neue','Helvetica',sans-serif";

    const offerTextBox = ref({ height: 0, width: 0 });
    const fontFitter = new AutoFittingText(
      offerTextBox.value.width,
      offerTextBox.value.height,
      {
        targetString: targetString.value,
        family: fontStyle.value.fontFamily || defaultFontFamily,
        lineHeight: 1.2,
        maxFontSize: 25,
        minFontSize: 10
      }
    );

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const fontMetrics = ref<any>();

    watchEffect(() => {
      fontFitter.width = offerTextBox.value.width;
      fontFitter.height = offerTextBox.value.height;
      fontFitter.targetString = targetString.value;
      fontFitter.family = fontStyle.value.fontFamily;

      fontMetrics.value = fontFitter.metrics;

      const fontDefString = `12px ${fontStyle.value.fontFamily}`;

      document.fonts &&
        !document.fonts.check(fontDefString, fontFitter.targetString) &&
        document.fonts
          .load(`12px ${fontStyle.value.fontFamily}`, fontFitter.targetString)
          .then(() => {
            // trigger a recalculation
            // should make fontfill more functional
            fontFitter.family = "";
            fontFitter.family = fontStyle.value.fontFamily;
            fontMetrics.value = fontFitter.metrics;
          });
    });

    const onResize = ({ height, width }) => {
      offerTextBox.value.width = width;
      offerTextBox.value.height = height;
    };

    return {
      classes,
      onResize,
      offerTextBox,
      offerStyle: computed(() => ({
        fontSize: Math.floor(fontMetrics.value.fontSize) + "px",
        lineHeight: 1.2,
        margin: 0,
        padding: 0
      })),
      offerHeader: computed(
        () => displayOffer.value && displayOffer.value.header
      ),
      offerLines: computed(() => fontMetrics.value.lines as string[])
    };
  }
});
</script>
